import { dollars } from './buildings';

/*
 * Terminal facilities, bought one by one inside a terminal on top of its level: more check-in
 * desks and security lanes (throughput), a lounge (comfort, and a little revenue per passenger)
 * and duty-free shops (retail). Counts are packed into the building's `facilities` field.
 */

/** Wire and save ids: append only. */
export const Facility = { CheckIn: 0, Security: 1, Lounge: 2, Shops: 3, Metro: 4 } as const;
export type FacilityId = (typeof Facility)[keyof typeof Facility];

export interface FacilityDef {
  readonly id: FacilityId;
  readonly key: string;
  /** Bit offset and width of its count in the packed field. */
  readonly shift: number;
  readonly max: number;
  /** Cents per unit. */
  readonly cost: number;
  /** Terminal level needed. */
  readonly level: number;
}

export const FACILITIES: readonly FacilityDef[] = [
  { id: Facility.CheckIn, key: 'checkIn', shift: 0, max: 3, cost: dollars(40_000), level: 1 },
  { id: Facility.Security, key: 'security', shift: 2, max: 2, cost: dollars(60_000), level: 2 },
  { id: Facility.Lounge, key: 'lounge', shift: 4, max: 1, cost: dollars(120_000), level: 2 },
  { id: Facility.Shops, key: 'shops', shift: 6, max: 2, cost: dollars(80_000), level: 1 },
  { id: Facility.Metro, key: 'metro', shift: 8, max: 1, cost: dollars(350_000), level: 3 },
];

/** Retail added per duty-free shop (percent). */
export const SHOP_PERCENT = 25;
/** With a lounge: comfort rating bonus (of 1000) and revenue per departing passenger. */
export const LOUNGE_COMFORT = 80;
export const LOUNGE_PER_PAX = dollars(2);
/**
 * With a metro station under a terminal: this share (percent) of the passengers come by metro
 * (no road), comfort and satisfaction bonuses, and the airport's share of the fares.
 */
export const METRO_SHARE_PERCENT = 20;
export const METRO_COMFORT = 60;
export const METRO_SATISFACTION = 2;
export const METRO_PER_PAX = 60;

export function facilityCount(packed: number, id: FacilityId): number {
  const def = FACILITIES[id];
  return def ? (packed >>> def.shift) & 3 : 0;
}

/** The packed field with one more of a facility. */
export function withFacility(packed: number, id: FacilityId): number {
  const def = FACILITIES[id];
  if (!def) return packed;
  const n = facilityCount(packed, id) + 1;
  return (packed & ~(3 << def.shift)) | (n << def.shift);
}

/**
 * The stages departing passengers pass, each with its own throughput (percent of the terminal
 * level's): check-in and bag drop, security, and passport control for international flights.
 * Every bank of check-in desks and every security lane widens its own stage only, so the slowest
 * stage sets the pace and the player has a real bottleneck to find.
 */
export const CHECKIN_STAGE_PERCENT = 35;
export const SECURITY_STAGE_PERCENT = 45;
export const PASSPORT_PERCENT = 70;

export interface StagePercents {
  checkIn: number;
  security: number;
  passport: number;
}

export function stagePercents(packed: number): StagePercents {
  return {
    checkIn: 100 + CHECKIN_STAGE_PERCENT * facilityCount(packed, Facility.CheckIn),
    security: 100 + SECURITY_STAGE_PERCENT * facilityCount(packed, Facility.Security),
    passport: PASSPORT_PERCENT,
  };
}

/** A terminal's throughput for domestic passengers (its slower stage), percent of its level's. */
export function capacityPercent(packed: number): number {
  const p = stagePercents(packed);
  return Math.min(p.checkIn, p.security);
}
